import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import styled from 'styled-components';

interface ImageViewProps {
  /** 이미지 URL */
  imageUrl?: string;
  /** 이미지 너비 */
  width?: number | string;
  /** 이미지 높이 */
  height?: number | string;
  /** 보더 반경 (기본값: 2px) */
  borderRadius?: number;
  /** 대체 텍스트 */
  alt?: string;
  /** 커스텀 클래스명 */
  className?: string;
  /** object-fit 스타일 (기본값: cover) */
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

const ImageView: React.FC<ImageViewProps> = ({
  imageUrl,
  width = '100%',
  height = 'auto',
  borderRadius = 2,
  alt = 'Image',
  className,
  objectFit = 'cover',
}) => {
  const [imgError, setImgError] = useState(false);

  // 이미지 URL이 변경될 때 에러 상태 초기화
  useEffect(() => {
    setImgError(false);
  }, [imageUrl]);

  const handleImageError = () => {
    devLog("이미지 로드 실패, 기본 이미지로 대체");
    setImgError(true);
  };

  // 이미지 URL이 있는지 확인 (에러 상태가 아닌 경우)
  const hasValidImage = imageUrl && !imgError;

  return (
    <Container
      width={width}
      height={height}
      $borderRadius={borderRadius}
      className={className}
    >
      {hasValidImage ? (
        <StyledImage
          src={imageUrl}
          alt={alt}
          onError={handleImageError}
          $objectFit={objectFit}
          $borderRadius={borderRadius}
        />
      ) : (
        <NoImageContainer>
          <NoDataImage
            src="/common/no_image.svg"
            alt="No data"
            $objectFit={objectFit}
            $borderRadius={borderRadius}
          />
          <NoDataText>이미지가 없습니다</NoDataText>
        </NoImageContainer>
      )}
    </Container>
  );
};

const Container = styled.div<{
  width: number | string;
  height: number | string;
  $borderRadius: number;
}>`
  width: ${props => typeof props.width === 'number' ? `${props.width}px` : props.width};
  height: ${props => typeof props.height === 'number' ? `${props.height}px` : props.height};
  border-radius: ${props => props.$borderRadius}px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  /* background-color: #f8f9fa; */
`;

const StyledImage = styled.img<{
  $objectFit: string;
  $borderRadius: number;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$objectFit};
  border-radius: ${props => props.$borderRadius}px;
`;

const NoImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 70%;
  height: 70%;
  gap: 6px;
  max-height: 200px;
`;

const NoDataImage = styled.img<{
  $objectFit: string;
  $borderRadius: number;
}>`
  width: 50%;
  height: 50%;
  object-fit: ${props => props.$objectFit};
  border-radius: ${props => props.$borderRadius}px;
  opacity: 0.5;
`;

const NoDataText = styled.span`
  color: #666666;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
`;

export default ImageView;
